<template>
  <div class="date-picker-wrapper">
    <VueDatePicker
      ref="datePicker"
      v-model="internalDate"
      :range="range"
      :auto-apply="true"
      :format="dateFormat"
      :min-date="minDateComputed"
      :max-date="maxDateComputed"
      :placeholder="label || 'Select start date'"
      :enable-time-picker="false"
      hide-input-icon
      :clearable="true"
      :teleport="false"
      :day-names="dayNames"
      month-name-format="long"
      @update:model-value="handleDateChange"
    />
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
  name: "date-picker",
  components: {
    VueDatePicker
  },
  props: {
    modelValue: [Object, String, null],
    value: [Object, String, null], // Support for Vue 2 style or compat
    setMinDate: Boolean,
    label: String,
    maxDate: String,
    range: Boolean,
    resetValue: Boolean
  },
  emits: ['update:modelValue', 'input'],
  data() {
    return {
      internalDate: null,
    };
  },
  computed: {
    dateFormat() {
      return "yyyy-MM-dd";
    },
    minDateComputed() {
      return this.setMinDate ? new Date() : null;
    },
    maxDateComputed() {
      return this.maxDate ? new Date(this.maxDate) : null;
    },
    dayNames() {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }
  },
  watch: {
    modelValue: {
      handler(val) {
        this.syncInternalValue(val);
      },
      immediate: true
    },
    value: {
      handler(val) {
        if (val !== undefined && this.modelValue === undefined) {
          this.syncInternalValue(val);
        }
      },
      immediate: true
    },
    resetValue(val) {
      if (val) {
        this.internalDate = null;
        this.emitChange(null);
      }
    }
  },
  methods: {
    syncInternalValue(val) {
      if (!val) {
        this.internalDate = null;
        return;
      }
      // Expecting { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } for range
      if (this.range && val.start && val.end) {
        this.internalDate = [this.parseDate(val.start), this.parseDate(val.end)];
      } else if (!this.range && val) {
        this.internalDate = this.parseDate(val);
      }
    },
    handleDateChange(val) {
      let payload = null;
      if (this.range && Array.isArray(val) && val.length === 2) {
        const [start, end] = val;
        payload = {
          start: this.formatDate(start),
          end: this.formatDate(end)
        };
      } else if (!this.range && val) {
        payload = this.formatDate(val);
      }
      
      this.emitChange(payload);
    },
    emitChange(payload) {
      this.$emit('update:modelValue', payload);
      this.$emit('input', payload);
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    parseDate(dateString) {
      if (!dateString) return null;
      // dateString is "YYYY-MM-DD"
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
  }
};
</script>

<style scoped>
.date-picker-wrapper {
  position: relative;
  display: block;
  width: 100%;
}

:deep(.dp__input) {
  border: 1px solid rgba(136, 136, 136, 1);
  border-radius: 10px;
  padding: 10px 40px 10px 18px !important;
  font-family: artifaktElementRegular !important;
  font-size: 14px;
  color: #000000;
  height: 40px;
  width: 100% !important;
}

:deep(.dp__input:focus) {
  border-color: #000000;
  outline: none;
}

/* Main component positioning */
:deep(.dp__main) {
  font-family: artifaktElementRegular !important;
  width: 100%;
}

/* Input wrapper positioning */
:deep(.dp__input_wrap) {
  position: relative;
  width: 100%;
}

/* Clear icon positioning - properly inside the input */
:deep(.dp__input_icons) {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  height: auto;
}

:deep(.dp__input_icon) {
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.dp__clear_icon) {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

:deep(.dp__clear_icon:hover) {
  opacity: 1;
}

/* Customize the primary color */
:deep(.dp__theme_light) {
  --dp-primary-color: #000000;
  --dp-primary-text-color: #ffffff;
}

/* Position the popup menu below the input */
:deep(.dp__menu_wrap) {
  position: relative;
}

:deep(.dp__menu) {
  z-index: 1000;
  margin-top: 4px;
  font-family: artifaktElementRegular !important;
  font-size: 12px;
  border-radius: 0;
}

:deep(.dp__menu_inner) {
  padding: 12px;
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

/* Apply artifaktElement font to all calendar elements */
:deep(.dp__calendar) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__month_year_row) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__calendar_header) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__calendar_header_item) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__cell_inner) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__month_year_select) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__instance_calendar) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__action_buttons) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__action_button) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__overlay_cell) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

:deep(.dp__overlay) {
  font-family: artifaktElementRegular !important;
  font-size: 12px;
}

/* Responsive adjustments */
@media screen and (max-width: 415px) {
  :deep(.dp__menu) {
    margin-top: 80px;
  }
  
  :deep(.dp__input) {
    font-size: 14px;
  }
}
</style>